










import { mapActions, mapGetters } from 'vuex'
import mixins from 'vue-typed-mixins'
import { ADD_TOAST_MESSAGE } from 'vuex-toast'
import { AxiosPromise } from 'axios'
import AbstractColumnLink from '@/abstract/ColumnLink'
import AbstractColumnValue from '@/abstract/ColumnValue'
import {
  ActiveScriptActionTypes,
  ActiveScriptStartPayload,
} from '@/store/modules/active-script/actions'
import { SCRIPTS__MAKE_ACTION } from '@/store/actions'
import {
  ScriptAction,
  ScriptStatus,
  ScriptPublishStatus,
  ScriptPublishStatusMessages,
} from '@/constants/scripts'
import { UserScope } from '@/models/users'
import { RouteNames } from '@/router'
import { CreateDraftPayload, CreateDraftResponse } from '@/models/scripts'

enum ButtonAction {
  Edit = 0,
  Start,
  Preview,
  Publish,
  CreateVersion,
}

export default mixins(AbstractColumnLink, AbstractColumnValue).extend({
  computed: {
    ...mapGetters(['canDoScope', 'hasActiveScript']),
    canStart(): boolean {
      return (
        this.canDoScope(UserScope.ScriptsUse) &&
        this.init.status === ScriptStatus.Publish &&
        !this.hasActiveScript
      )
    },
    isPublished(): boolean {
      return this.init.status === ScriptStatus.Publish
    },
    actionType(): ButtonAction | undefined {
      if (this.isPublished) {
        return this.canDoScope(UserScope.ScriptsUse)
          ? ButtonAction.Start
          : ButtonAction.Preview
      }

      if (!this.canDoScope(UserScope.ScriptsPublish)) {
        return
      }

      if (this.init.draftId) {
        return this.canDoScope(UserScope.ScriptsEdit)
          ? ButtonAction.Edit
          : ButtonAction.Publish
      }

      if (this.init.hasVersions) {
        return ButtonAction.Publish
      }

      return ButtonAction.CreateVersion
    },
    buttonClass(): '-green' | '-purple' | '-gray' {
      switch (this.actionType) {
        case ButtonAction.Edit:
          return '-purple'
        case ButtonAction.Start:
          return '-green'
        case ButtonAction.Preview:
          return '-purple'
        case ButtonAction.Publish:
          return '-green'
        case ButtonAction.CreateVersion:
          return '-green'
        default:
          return '-gray'
      }
    },
    buttonText(): string {
      switch (this.actionType) {
        case ButtonAction.Edit:
          return this.$tc('common.edit')
        case ButtonAction.Start:
          return this.$tc('common.start')
        case ButtonAction.Preview:
          return this.$tc('common.preview')
        case ButtonAction.Publish:
          return this.$tc('common.publish')
        case ButtonAction.CreateVersion:
          return this.$tc('script.actions.createVersion')
        default:
          return ''
      }
    },
  },
  methods: {
    ...mapActions([SCRIPTS__MAKE_ACTION, ScriptAction.Draft]),
    makeAction(
      action: ScriptAction,
    ): AxiosPromise<{ publish: ScriptPublishStatus } | boolean> {
      return this[SCRIPTS__MAKE_ACTION]({
        action,
        id: this.init.id,
        instance: this.init.instanceId,
      })
    },
    publish(): void {
      this.makeAction(ScriptAction.Publish).then(response => {
        let type = 'info'
        let text = ''
        if (
          typeof response.data !== 'boolean' &&
          typeof response.data.publish === 'number'
        ) {
          text = ScriptPublishStatusMessages[response.data.publish]
          if (
            [
              ScriptPublishStatus.Not,
              ScriptPublishStatus.NotFound,
              ScriptPublishStatus.NotVersioned,
            ].includes(response.data.publish)
          ) {
            type = 'warning'
          }
        }
        this.$store.dispatch(ADD_TOAST_MESSAGE, {
          text,
          type,
        })
      })
    },
    createDraft() {
      const { id, instanceId } = this.init

      const payload: CreateDraftPayload = {
        id,
        instance: instanceId,
      }

      this[ScriptAction.Draft](payload).then(
        (response: CreateDraftResponse) => {
          const { id } = this.init
          this.$router.push({
            name: RouteNames.ScriptSchemeEdit,
            params: { scriptId: id, instanceId: response.id },
          })
        },
      )
    },
    async startScript() {
      const { id } = this.init
      const startScriptPayload: ActiveScriptStartPayload = { scriptId: id }
      await this.$store.dispatch(
        ActiveScriptActionTypes.Start,
        startScriptPayload,
      )
      this.$router.push({ name: RouteNames.ActiveScript })
    },
    async buttonAction() {
      const { id, draftId } = this.init

      switch (this.actionType) {
        case ButtonAction.Edit:
          if (!draftId) {
            return
          }

          return this.$router.push({
            name: RouteNames.ScriptSchemeEdit,
            params: { scriptId: id, instanceId: draftId },
          })
        case ButtonAction.Start:
          return this.startScript()
        case ButtonAction.Preview:
          return this.$router.push({
            name: RouteNames.ScriptPage,
            params: { scriptId: id },
          })
        case ButtonAction.Publish:
          return this.publish()
        case ButtonAction.CreateVersion:
          return this.createDraft()
      }
    },
  },
})
